import React, { useState, useEffect, useRef } from "react";
import { colors, fonts } from "./theme";
import { Eyebrow, PhoneLink } from "./ui";
import { TextField, TextArea } from "./form";
import { validateProductForm, PRODUCT_FIELD_ORDER } from "./validation";
import { openWhatsApp } from "./whatsapp";
import {
  canSubmitDirectly,
  productFields,
  productMessage,
  submitEnquiry,
} from "./enquiry";

const EMPTY = {
  name: "",
  contact: "",
  company: "",
  city: "",
  quantity: "",
  size: "",
  grammage: "",
  description: "",
};

// Both buttons share this. flex-basis 160px means they sit side by side on a
// roomy card and wrap to one per row once the card narrows, without needing a
// media query. minHeight holds the 44px touch floor independently of the
// padding token, so a future padding tweak can't quietly shrink the target.
const btnStyle = {
  minHeight: "46px",
  padding: "var(--btn-py) var(--btn-px)",
  borderRadius: "2px",
  fontSize: "var(--btn-fs)",
  fontWeight: 500,
  cursor: "pointer",
  flex: "1 1 160px",
};

// Popup that collects requirement details for one product and sends them
// either straight to us or through WhatsApp, at the visitor's choice.
export default function ProductEnquiryModal({ item, categoryName, onClose }) {
  const [form, setForm] = useState(EMPTY);
  // idle → sending → sent. The WhatsApp route closes the modal instead of
  // resolving to a state, because the visitor's attention has left for WhatsApp.
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  // Which fields have been blurred, or — after a failed submit attempt — all
  // of them at once. See the matching comment in Contact.jsx; the two forms
  // share the same touched/error-timing model.
  const [touched, setTouched] = useState({});
  const fieldRefs = useRef({});
  const set = (k) => (value) => setForm((f) => ({ ...f, [k]: value }));
  const setRef = (k) => (el) => { fieldRefs.current[k] = el; };
  const onBlurField = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const errors = validateProductForm(form);
  const shownError = (k) => (touched[k] ? errors[k] : undefined);

  const focusFirstInvalid = (formErrors) => {
    setTouched(Object.fromEntries(PRODUCT_FIELD_ORDER.map((k) => [k, true])));
    const firstInvalid = PRODUCT_FIELD_ORDER.find((k) => formErrors[k]);
    if (firstInvalid) fieldRefs.current[firstInvalid]?.focus();
  };

  // Close on Escape and lock background scroll while open.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(errors);
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await submitEnquiry({
        kind: "Product enquiry",
        subject: `Product enquiry — ${item.name}`,
        replyTo: form.contact,
        pairs: productFields(categoryName, item, form),
      });
      setStatus("sent");
    } catch (err) {
      setError(err.message);
      setStatus("idle");
    }
  };

  // A parallel route rather than a fallback, so it validates the same
  // fields — through the same validator the submit path uses, now that
  // native `required` and reportValidity() are gone — and then hands off.
  // The modal closes because WhatsApp now owns the thread.
  const onWhatsApp = () => {
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(errors);
      return;
    }
    openWhatsApp(productMessage(categoryName, item, form));
    onClose();
  };

  return (
    /* The overlay is the scroller, not the card.
       The card used to cap itself at 90vh inside a position:fixed overlay. On
       iOS those two lengths are not the same box: vh resolves against the
       *large* viewport (toolbars retracted) while the fixed overlay is laid
       out in the *small* one, so the card came out taller than its container
       and place-items:center centred the overflow — clipping ~38px off the
       top, which is precisely where the close button sits. Unreachable.
       Letting the overlay scroll and giving the card no height cap removes the
       vh unit from the problem entirely. `margin: auto 0` still centres the
       card while it fits, because auto margins collapse to zero once the free
       space goes negative, at which point align-items:start takes over. */
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(30,26,22,0.55)",
        display: "grid",
        placeItems: "start center",
        overflowY: "auto",
        padding: "20px 16px",
      }}
    >
      {/* .se-panel trims the 26px side padding below 560px — with the overlay's
          own 16px gutters it left 228px of usable content on a 320px screen. */}
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className="se-panel"
        style={{
          width: "min(500px, 100%)",
          maxHeight: "none",
          margin: "auto 0",
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "6px",
          padding: "26px 26px 30px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
          <div>
            <Eyebrow style={{ fontSize: "11px" }}>{categoryName}</Eyebrow>
            <h3 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h3)", fontWeight: 500, margin: "6px 0 0" }}>
              Enquire — {item.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              // 34x34 was 10px under the touch floor on the one control that
              // dismisses a modal. The box is 44x44; a 5px transparent border
              // holds the extra hit area outside the visible square, and the
              // 1px outline is redrawn as an inset shadow, which paints at the
              // padding edge — so it still reads as the same 34px button.
              width: "44px",
              height: "44px",
              border: "5px solid transparent",
              borderRadius: "7px",
              boxShadow: `inset 0 0 0 1px ${colors.border}`,
              background: "transparent",
              backgroundClip: "padding-box",
              color: colors.muted,
              fontSize: "20px",
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        {status === "sent" ? (
          <div style={{ padding: "26px 4px 8px", textAlign: "center" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#EAF1E4",
                border: "1px solid #CBDDBB",
                color: "#4a7a34",
                display: "grid",
                placeItems: "center",
                fontSize: "22px",
                margin: "0 auto 16px",
              }}
            >
              ✓
            </div>
            <h4 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h4)", fontWeight: 500, margin: "0 0 8px" }}>
              Enquiry received
            </h4>
            <p style={{ fontSize: "var(--fs-base)", color: colors.muted, lineHeight: 1.55, margin: "0 auto 20px", maxWidth: "38ch" }}>
              Thanks — we have your requirement for {item.name} and will reply within one
              working day. For anything urgent, WhatsApp us on{" "}
              <PhoneLink style={{ color: colors.brown, fontWeight: 600 }} />.
            </p>
            <button type="button" onClick={onClose} className="se-btn-dark" style={{ ...btnStyle, flex: "0 0 auto" }}>
              Close
            </button>
          </div>
        ) : (
        <>
        <p style={{ fontSize: "var(--fs-base)", color: colors.muted, lineHeight: 1.5, margin: "12px 0 20px" }}>
          {canSubmitDirectly
            ? "Tell us what you need — send it straight to us, or take the same details over to WhatsApp."
            : "Tell us what you need — we'll open WhatsApp with your enquiry ready to send."}
        </p>

        <form onSubmit={submit} noValidate>
          {/* auto-fit + minmax(min(200px,100%),1fr) collapses the pairs to one
              column from the card's own width rather than a viewport query,
              and the min() keeps the 200px floor from overflowing a narrower
              column and pushing the dialog sideways. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(200px,100%),1fr))", gap: "16px" }}>
            {/* Name and contact are new here. On WhatsApp the sender's own
                number was the reply address, but an enquiry that arrives by
                email needs to carry one, or there is no way to answer it. */}
            <TextField
              ref={setRef("name")}
              id="product-enquiry-name"
              label="Name"
              error={shownError("name")}
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={set("name")}
              onBlur={onBlurField("name")}
            />
            <TextField
              ref={setRef("contact")}
              id="product-enquiry-contact"
              label="Phone / Email"
              error={shownError("contact")}
              type="text"
              placeholder="How to reach you"
              value={form.contact}
              onChange={set("contact")}
              onBlur={onBlurField("contact")}
            />
            <TextField
              ref={setRef("company")}
              id="product-enquiry-company"
              label="Company name"
              error={shownError("company")}
              type="text"
              placeholder="Company name"
              value={form.company}
              onChange={set("company")}
              onBlur={onBlurField("company")}
            />
            <TextField
              ref={setRef("city")}
              id="product-enquiry-city"
              label="Delivery city"
              error={shownError("city")}
              type="text"
              placeholder="e.g. New Delhi"
              value={form.city}
              onChange={set("city")}
              onBlur={onBlurField("city")}
            />
            <TextField
              ref={setRef("quantity")}
              id="product-enquiry-quantity"
              label="Quantity"
              error={shownError("quantity")}
              type="text"
              placeholder="e.g. 500 sheets / 20 reams"
              value={form.quantity}
              onChange={set("quantity")}
              onBlur={onBlurField("quantity")}
            />
            <TextField
              ref={setRef("grammage")}
              id="product-enquiry-grammage"
              label="Grammage"
              error={shownError("grammage")}
              type="text"
              placeholder="e.g. 80 gsm"
              value={form.grammage}
              onChange={set("grammage")}
              onBlur={onBlurField("grammage")}
            />
          </div>

          <div style={{ marginTop: "16px" }}>
            <TextField
              ref={setRef("size")}
              id="product-enquiry-size"
              label="Size"
              error={shownError("size")}
              type="text"
              placeholder="e.g. 23 × 36 in / SRA3"
              value={form.size}
              onChange={set("size")}
              onBlur={onBlurField("size")}
            />
          </div>

          <div style={{ marginTop: "16px" }}>
            <TextArea
              ref={setRef("description")}
              id="product-enquiry-description"
              label="Description"
              error={shownError("description")}
              rows="3"
              placeholder="Finish, colour, brand or any other detail…"
              value={form.description}
              onChange={set("description")}
              onBlur={onBlurField("description")}
            />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "22px" }}>
            {/* Hidden when a build has no enquiry backend configured, so the
                popup falls back to exactly its previous WhatsApp-only self. */}
            {canSubmitDirectly && (
              <button
                type="submit"
                className="se-btn-dark"
                disabled={status === "sending"}
                style={{
                  ...btnStyle,
                  opacity: status === "sending" ? 0.65 : 1,
                  cursor: status === "sending" ? "progress" : "pointer",
                }}
              >
                {status === "sending" ? "Sending…" : "Send enquiry →"}
              </button>
            )}
            <button
              type={canSubmitDirectly ? "button" : "submit"}
              onClick={canSubmitDirectly ? onWhatsApp : undefined}
              className={canSubmitDirectly ? "se-btn-ghost" : "se-btn-dark"}
              disabled={status === "sending"}
              style={btnStyle}
            >
              Send on WhatsApp
            </button>
          </div>

          {error && (
            <p
              role="alert"
              style={{
                fontSize: "var(--fs-meta)",
                lineHeight: 1.55,
                color: colors.danger,
                background: colors.dangerBg,
                border: `1px solid ${colors.dangerBorder}`,
                borderRadius: "3px",
                padding: "10px 12px",
                margin: "14px 0 0",
              }}
            >
              {error} You can send it on WhatsApp instead, or message us on{" "}
              <PhoneLink style={{ color: colors.danger, fontWeight: 600 }} />.
            </p>
          )}
        </form>
        </>
        )}
      </div>
    </div>
  );
}
